import {
  Colors,
  Flex,
  IconHeart,
  IconHeartFilled,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import type { MarketRankingItem } from 'shared/src/api';
import { isNotNil, useNavigator } from 'shared/src/utils';
import React, { useMemo } from 'react';

const PROFILE_SIZE = 44;

interface MarketRankingCardProps {
  item: MarketRankingItem;
  isFavorite: boolean;
  onPress: () => void;
  onPressFavorite: () => void;
}

export const MarketRankingCard = ({
  item,
  isFavorite,
  onPress,
  onPressFavorite,
}: MarketRankingCardProps) => {
  const { navigator } = useNavigator();

  const rankDiff = useMemo(
    () => (isNotNil(item.last_rank) ? item.last_rank - item.rank : null),
    [item.last_rank, item.rank],
  );

  const { width: screenWidth } = useScreenSize();

  const imageSize = useMemo(() => {
    return (screenWidth - 16 * 2 - 2 * 2) / 3;
  }, [screenWidth]);

  return (
    <Flex padding={{ horizontal: 16, bottom: 12 }} onPress={onPress}>
      <Flex.Row
        alignItems="center"
        gap={12}
        padding={{ vertical: 16 }}
        onPress={() => {
          navigator.moveToMarketHome({ sno: item.sno });
        }}
      >
        {/* 랭킹 */}
        <Flex width={32} alignItems="center">
          <Typography
            type={item.filtered_rank <= 3 ? 'subtitle1' : 'body1'}
            color={
              item.filtered_rank <= 3
                ? 'content_pink_primary'
                : 'content_primary'
            }
          >
            {item.filtered_rank}
          </Typography>
          <RankFluctuation rankDiff={rankDiff} lastRank={item.last_rank} />
        </Flex>

        {/* 프로필 이미지 */}
        <Flex
          width={PROFILE_SIZE}
          height={PROFILE_SIZE}
          borderRadius={PROFILE_SIZE / 2}
          overflow="hidden"
          borderWidth={0.5}
          borderColor="border_primary"
        >
          <RemoteImage
            uri={item.image}
            width={PROFILE_SIZE}
            height={PROFILE_SIZE}
          />
        </Flex>

        {/* 마켓 정보 */}
        <Flex flex={1} gap={4}>
          <Flex.Row gap={4} alignItems={'center'}>
            <Typography
              type="subtitle1"
              color="content_primary"
              numberOfLines={1}
            >
              {item.name}
            </Typography>
            {isNotNil(item.highlight) && (
              <Typography
                type="body4"
                color="content_blue_primary"
                numberOfLines={1}
              >
                {item.highlight}
              </Typography>
            )}
          </Flex.Row>
          {isNotNil(item.hash_tag) && (
            <Typography type="body3" color="content_tertiary" numberOfLines={1}>
              {item.hash_tag}
            </Typography>
          )}
        </Flex>

        {/* 찜 버튼 */}
        <Flex alignItems="center" gap={2} onPress={onPressFavorite}>
          {isFavorite ? (
            <IconHeartFilled size={24} fill={Colors.content_pink_primary} />
          ) : (
            <IconHeart size={24} fill={Colors.content_pink_primary} />
          )}
          <Typography type="body4" color="content_pink_primary">
            {formatCount(item.favorites_count)}
          </Typography>
        </Flex>
      </Flex.Row>

      {/* 상품 이미지 */}
      {item.goods_images.length > 0 && (
        <Flex>
          <Flex.Row gap={2}>
            {item.goods_images.slice(0, 3).map(imageUrl => (
              <RemoteImage
                key={imageUrl}
                uri={imageUrl}
                width={imageSize}
                height={imageSize * 1.12}
                borderRadius={'small'}
              />
            ))}
          </Flex.Row>
        </Flex>
      )}
    </Flex>
  );
};

const RankFluctuation = ({
  rankDiff,
  lastRank,
}: {
  rankDiff: number | null;
  lastRank: number | null;
}) => {
  if (lastRank === null) {
    return (
      <Typography type="body5" color="content_pink_primary">
        NEW
      </Typography>
    );
  }

  if (rankDiff === null || rankDiff === 0) return null;

  return (
    <Flex.Row alignItems="center" gap={2}>
      <Typography
        type="body5"
        color={rankDiff > 0 ? 'content_pink_primary' : 'content_secondary'}
      >
        {rankDiff > 0 ? '▲' : '▼'}
      </Typography>
      <Typography
        type="body5"
        color={rankDiff > 0 ? 'content_pink_primary' : 'content_secondary'}
      >
        {Math.abs(rankDiff)}
      </Typography>
    </Flex.Row>
  );
};

const formatCount = (count: number): string => {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}만`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}천`;
  }
  return count.toLocaleString();
};
